/**
 * Vuetify 3 plugin configuration
 * NOTE: Components and directives are auto-imported by vite-plugin-vuetify (tree-shaking)
 * Only used components are bundled, reducing initial bundle size significantly
 */
import { createVuetify } from 'vuetify'
import '@mdi/font/css/materialdesignicons.css'
import 'vuetify/styles'

// Anti-FOUC: pick the STARTING Vuetify theme from the slim branding cache
// (written by stores/branding.js as `mode`), synchronously at import time —
// i.e. BEFORE the app mounts. Without this Vuetify always boots 'light' and a
// dark user sees a white flash for ~1s until branding.applyToDom() flips the
// global theme name after the async bootstrap (TICK-00133).
function _initialThemeName() {
  try {
    const raw = localStorage.getItem('branding_v1')
    if (!raw) return 'light'
    const b = JSON.parse(raw)
    return b && b.mode === 'dark' && b.de ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

export default createVuetify({
  theme: {
    defaultTheme: _initialThemeName(),
    themes: {
      light: {
        colors: {
          primary: '#1e3a5f',      // Navy - sidebar, headers
          accent: '#4dd4ac',       // Teal - CTA buttons, highlights
          secondary: '#6b7280',    // Medium gray
          success: '#4dd4ac',      // Same as accent
          warning: '#ff9966',      // Soft orange
          error: '#ef4444',        // Bright red
          info: '#3b82f6',         // Blue
          background: '#f5f7fa',   // Light gray-blue background
          surface: '#ffffff',      // White cards
          'surface-variant': '#eef2f7',
          'on-surface-variant': '#1f2937',
          'on-primary': '#ffffff',
          'on-accent': '#ffffff',
          'on-success': '#ffffff',
          'on-warning': '#ffffff',
          'on-error': '#ffffff',
          'god-mode': '#dc2626',   // God mode indicator
        }
      },
      // Full dark theme. Branding store may override brand tokens per domain,
      // everything else falls back to these values (Material 3 dark guidance:
      // desaturated brand colors, elevated slate surfaces, soft status colors).
      dark: {
        dark: true,
        colors: {
          primary: '#3b82f6',          // Desaturated blue (navy is too dark on dark)
          accent: '#4dd4ac',           // Brand teal stays
          secondary: '#94a3b8',
          success: '#34d399',
          warning: '#fbbf24',
          error: '#f87171',
          info: '#60a5fa',
          background: '#0f172a',       // slate-900
          surface: '#1e293b',          // slate-800 cards
          'surface-bright': '#334155', // inputs / elevated
          'surface-light': '#283548',
          'surface-variant': '#283548',
          'on-surface-variant': '#cbd5e1',
          'on-primary': '#ffffff',
          'on-accent': '#06281e',
          'on-success': '#06281e',
          'on-warning': '#3b2a05',
          'on-error': '#450a0a',
          'on-background': '#f1f5f9',
          'on-surface': '#f1f5f9',
          'god-mode': '#ef4444',
        },
        variables: {
          'border-color': '#ffffff',
          'border-opacity': 0.10,
          'theme-on-surface-variant': '#cbd5e1',
        }
      }
    }
  },
  defaults: {
    VBtn: {
      style: [{ 
        'text-transform': 'none',
        'letter-spacing': 'normal',
        'font-weight': '600'
      }],
      rounded: 'lg',
      elevation: 0,
    },
    VCard: {
      rounded: 'xl',
      elevation: 2,
    },
    VChip: {
      rounded: 'lg'
    },
    VTextField: {
      rounded: 'lg',
      variant: 'outlined',
      autocomplete: 'off'
    },
    // Kill native browser autofill dropdowns everywhere: they overlap our own
    // menus (VAutocomplete/VCombobox) and paste unrelated saved values.
    // Login fields that NEED autofill declare autocomplete explicitly
    // (e.g. autocomplete="tel" / "current-password") which overrides these.
    VTextarea: {
      autocomplete: 'off'
    },
    VAutocomplete: {
      autocomplete: 'off'
    },
    VCombobox: {
      autocomplete: 'off'
    },
    VSelect: {
      autocomplete: 'off'
    },
    VForm: {
      autocomplete: 'off'
    }
  }
})

